import { t } from "i18next";
import React, { useState, useEffect } from "react";
import { Trans } from "react-i18next";

import Select from "react-select";

import LookupsAPI from "../../API/lookups.api";
import { ACCESS_OPTIONS } from "../../utils/constants";
import {
    getReactSelectSmallStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import ProductTags from "../Tags/ProductTags";
import SelectTableForm from "./SelectTableForm";

const UserAccessForm = ({
    user,
    selectedModels,
    setSelectedModels,
    mappedModelAccess,
    setMappedModelAccess,
}) => {
    const [models, setModels] = useState([]);
    const [modelsCount, setModelsCount] = useState(0);

    const headers = [
        t("modelName"),
        t("parameters"),
        t("permission"),
        t("remove"),
    ];

    useEffect(() => {
        if (user) {
            fetchUserAccess().then((access) => {
                fetchModels(access || {});
            });
        }
    }, [user]);

    const fetchUserAccess = async () => {
        const result = await LookupsAPI.getAccessByUserId(user.id);
        let tempAccesses = {};
        if (result) {
            result.forEach((access) => {
                tempAccesses[access.modelId] = access.write
                    ? ACCESS_OPTIONS.write
                    : ACCESS_OPTIONS.read;
            });
        }
        setMappedModelAccess(tempAccesses);
        return tempAccesses;
    };

    const fetchModels = async (mappedAccess) => {
        try {
            const result = await LookupsAPI.getModels();
            const newSelectedModels = [...selectedModels];
            const newResult = [];

            result.forEach((model) => {
                const mappedModel = {
                    label: model.modelName,
                    value: model,
                    creator: model.createdById === user.id,
                };

                if (mappedAccess[model.id]) {
                    const AccessValue = mappedAccess[model.id].value;
                    newSelectedModels.push({
                        ...mappedModel,
                        value: {
                            ...mappedModel.value,
                            write: AccessValue === ACCESS_OPTIONS.write.value,
                            read: AccessValue === ACCESS_OPTIONS.read.value,
                        },
                    });
                } else {
                    newResult.push(mappedModel);
                }
            });

            setModelsCount(newSelectedModels.length);
            setSelectedModels(newSelectedModels);
            setModels(newResult);
        } catch (error) {
            console.log(error);
        }
    };

    const handleSelectionChanged = (model) => {
        if (model) {
            model.value.read = true;
            const newModels = [...models];
            const newSelected = [...selectedModels];

            const modelId = model.value.id;
            if (isExists(modelId)) {
                // handle if the model already exists in the selected model, so we can change the isDeleted flag only
                let selectedModel = newSelected.find(
                    (m) => m.value.id === modelId
                );
                selectedModel.value.isDeleted = false;
            } else {
                newSelected.unshift(model);
            }

            // remove the selection from dropdown options
            models.forEach((m, index) => {
                const value = m.value.id;
                if (value === model.value.id) {
                    newModels.splice(index, 1);
                }
            });
            setModels(newModels);
            setModelsCount(modelsCount + 1);
            setSelectedModels(newSelected);
        }
    };

    const handleDelete = (index) => {
        let selectedModel = selectedModels[index].value;
        const newSelected = [...selectedModels];

        if (isExists(selectedModel?.id)) {
            selectedModel.isDeleted = true;
        } else {
            newSelected.splice(index, 1);
        }

        setModels([
            ...models,
            { label: selectedModel.modelName, value: selectedModel },
        ]);
        setSelectedModels(newSelected);
        setModelsCount(modelsCount - 1);
    };

    const isExists = (modelId) => {
        return mappedModelAccess[modelId] || null;
    };

    const handleChangeAccessType = (accessType, index) => {
        const write = accessType.value === ACCESS_OPTIONS.write.value;
        const newSelected = [...selectedModels];
        newSelected[index].value = {
            ...newSelected[index].value,
            read: true,
            write,
        };

        setSelectedModels(newSelected);
    };

    const isOwner = (model, type = "Owner") => {
        return (model?.creator && type) || "";
    };

    const isReadOrWrite = (model) => {
        return model?.value?.write ? ACCESS_OPTIONS.write : ACCESS_OPTIONS.read;
    };

    const getTableLabel = () => {
        return (
            <>
                <Trans i18nKey="giveAccessFor">Give Access for</Trans>
                <span className="text-primary-orange capitalize">
                    {` ${user?.role} `}
                </span>
                <Trans i18nKey="on">on</Trans>
                <span className="text-primary-orange ml-1">
                    ({modelsCount})
                </span>
            </>
        );
    };

    const keys = [
        {
            element: (model) => (
                <div className="flex items-center content-center">
                    <img
                        src={
                            model?.value?.image ||
                            require("../../assets/images/neural.png").default
                        }
                        className="table-user-img ml-6"
                        alt="model Image"
                    />
                    <div className=" py-3 whitespace-wrap">
                        {model?.value?.modelName}
                    </div>
                </div>
            ),
        },
        {
            element: (model) => (
                <div className="px-6 py-3 whitespace-wrap">
                    <ProductTags
                        center={true}
                        tags={model?.value?.modelParameters}
                        keyName="parameterName"
                    />
                </div>
            ),
        },
        {
            element: (model, index) => (
                <div className="px-6 py-3 whitespace-wrap">
                    {isOwner(model) || (
                        <Select
                            options={Object.values(ACCESS_OPTIONS)}
                            value={isReadOrWrite(model)}
                            styles={getReactSelectSmallStyling(false)}
                            onChange={(selected) => {
                                handleChangeAccessType(selected, index);
                            }}
                            theme={getReactSelectTheme}
                        />
                    )}
                </div>
            ),
        },
        {
            element: (model, index) => (
                <div
                    className={`px-6 py-3 whitespace-wrap ${isOwner(
                        model,
                        "disabled"
                    )}`}
                    disabled={isOwner(model, "disabled")}
                >
                    {isOwner(model, "---") || (
                        <button
                            onClick={() => handleDelete(index)}
                            className="table-btn table-btn-orange m-0"
                        >
                            <i className="fas fa-trash-alt" />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <SelectTableForm
                mainSelect={{
                    label: <Trans i18nKey="models">Models</Trans>,
                    options: models,
                    onChangeHandler: handleSelectionChanged,
                    placeholder: t("modelSelectPlaceHolder"),
                }}
                tableLabel={getTableLabel()}
                selectedData={selectedModels}
                emptySelectedPlaceholder={t("seletModelToBeAdded")}
                keys={keys}
                headers={headers}
            />
        </>
    );
};

export default UserAccessForm;
